// Analytics and data processing utilities

/**
 * Try to extract phased win percentages from API response; fallback is handled by caller
 */
export const parsePhaseWinPercentages = (response, responseText) => {
  try {
    // 1) Check for structured fields in common locations
    const candidate = (
      response?.search_results?.win_percentage_phases ||
      response?.win_percentage_phases ||
      response?.search_results?.analytics?.win_percentage_phases ||
      response?.analytics?.win_percentage_phases ||
      null
    );
    const scalePct = (v) => {
      const num = Number(v);
      if (!Number.isFinite(num)) return 0;
      // If value is in [0,1], interpret as ratio and scale to percent
      return num <= 1 ? num * 100 : num;
    };
    const normalizeObjToPhases = (obj) => {
      const getPct = (o, keyVariants) => {
        for (const k of keyVariants) {
          const v = o?.[k];
          if (v != null) return Number(v);
        }
        return null;
      };
      const mk = (label, o) => {
        if (!o || typeof o !== 'object') return { label, pctA: 0, pctB: 0 };
        const pctA = getPct(o, ['A','a','p1','player_1','pctA','pct_a','playerA']);
        const pctB = getPct(o, ['B','b','p2','player_2','pctB','pct_b','playerB']);
        return { label, pctA: Math.max(0, Math.min(100, scalePct(pctA))), pctB: Math.max(0, Math.min(100, scalePct(pctB))) };
      };
      // Object with keys initial/mid/final
      return [
        mk('Initial', obj.initial || obj.early || obj.start),
        mk('Mid', obj.mid || obj.middle),
        mk('Final', obj.final || obj.late || obj.end),
      ];
    };
    const normalizeArrayToPhases = (arr) => {
      // Expect array of 3 with label/pctA/pctB or objects with phase
      const labelFor = (i, it) => it?.label || ['Initial','Mid','Final'][i] || `Phase ${i+1}`;
      return arr.slice(0,3).map((it, i) => ({
        label: labelFor(i, it),
        pctA: Math.max(0, Math.min(100, scalePct(it?.pctA ?? it?.A ?? it?.p1 ?? it?.player_1 ?? 0))),
        pctB: Math.max(0, Math.min(100, scalePct(it?.pctB ?? it?.B ?? it?.p2 ?? it?.player_2 ?? 0))),
      }));
    };
    if (candidate) {
      if (Array.isArray(candidate)) {
        const phases = normalizeArrayToPhases(candidate);
        if (phases.length) return phases;
      } else if (typeof candidate === 'object') {
        const phases = normalizeObjToPhases(candidate);
        if (phases.some(p => (p.pctA || p.pctB))) return phases;
      }
    }

    // 2) Try to parse from response text in a simple way
    if (responseText) {
      // Normalize text to be regex-friendly across different dash types and newlines
      const text = String(responseText)
        .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-') // various dashes to '-'
        .replace(/\r\n/g, '\n');
      // Strategy A: explicit Phase 1/2/3 lines with A: and B:
      const phaseMap = [
        { label: 'Initial', num: 1 },
        { label: 'Mid', num: 2 },
        { label: 'Final', num: 3 },
      ];
      const parsedByNumber = [];
      for (const pm of phaseMap) {
        const reAB = new RegExp(`phase\\s*${pm.num}[\\s\\S]{0,200}?A\\s*[:\\-]?\\s*(\\d{1,3}(?:\\.\\d+)?)%[\\s\\S]{0,120}?B\\s*[:\\-]?\\s*(\\d{1,3}(?:\\.\\d+)?)%`, 'i');
        const reBA = new RegExp(`phase\\s*${pm.num}[\\s\\S]{0,200}?B\\s*[:\\-]?\\s*(\\d{1,3}(?:\\.\\d+)?)%[\\s\\S]{0,120}?A\\s*[:\\-]?\\s*(\\d{1,3}(?:\\.\\d+)?)%`, 'i');
        let m = text.match(reAB);
        if (m) {
          const a = Math.max(0, Math.min(100, scalePct(m[1])));
          const b = Math.max(0, Math.min(100, scalePct(m[2])));
          parsedByNumber.push({ label: pm.label, pctA: a, pctB: b });
          continue;
        }
        m = text.match(reBA);
        if (m) {
          const b = Math.max(0, Math.min(100, scalePct(m[1])));
          const a = Math.max(0, Math.min(100, scalePct(m[2])));
          parsedByNumber.push({ label: pm.label, pctA: a, pctB: b });
          continue;
        }
        parsedByNumber.push({ label: pm.label, pctA: 0, pctB: 0 });
      }
      if (parsedByNumber.some(p => p.pctA || p.pctB)) return parsedByNumber;

      // Strategy B: words Initial/Mid/Final followed by two percentages
      const phaseNames = [
        { key: 'Initial', alts: ['initial','early','start'] },
        { key: 'Mid', alts: ['mid','middle'] },
        { key: 'Final', alts: ['final','late','end'] },
      ];
      const out = [];
      for (const ph of phaseNames) {
        const re = new RegExp(`(?:${ph.alts.join('|')})[\\s\\S]{0,200}?[\\s\\S]*?A\\s*[:\\-]?\\s*(\\d{1,3}(?:\\.\\d+)?)%[\\s\\S]{0,120}?B\\s*[:\\-]?\\s*(\\d{1,3}(?:\\.\\d+)?)%`, 'i');
        const m = text.match(re);
        if (m) {
          const a = Math.max(0, Math.min(100, scalePct(m[2])));
          const b = Math.max(0, Math.min(100, scalePct(m[3])));
          out.push({ label: ph.key, pctA: a, pctB: b });
        } else {
          // fallback: any two percentages near the phase word, order unknown
          const re2 = new RegExp(`(?:${ph.alts.join('|')})[\\s\\S]{0,200}?(\\d{1,3}(?:\\.\\d+)?)%[\\s\\S]{0,120}?(\\d{1,3}(?:\\.\\d+)?)%`, 'i');
          const m2 = text.match(re2);
          if (m2) {
            const a = Math.max(0, Math.min(100, scalePct(m2[1])));
            const b = Math.max(0, Math.min(100, scalePct(m2[2])));
            out.push({ label: ph.key, pctA: a, pctB: b });
          } else {
            out.push({ label: ph.key, pctA: 0, pctB: 0 });
          }
        }
      }
      if (out.some(p => p.pctA || p.pctB)) return out;
    }
  } catch {}
  return null;
};

/**
 * Try to extract overall win percentages (A and B) from API response or raw text.
 * Returns an object like { pctA: number, pctB: number } in [0,100] or null if not found.
 */
export const parseOverallWinPercentages = (response, responseText) => {
  try {
    // 1) Check structured fields if any exist in common locations
    const candidate = (
      response?.search_results?.win_percentage_overall ||
      response?.win_percentage_overall ||
      response?.search_results?.analytics?.win_percentage_overall ||
      response?.analytics?.win_percentage_overall ||
      null
    );
    const scalePct = (v) => {
      const num = Number(v);
      if (!Number.isFinite(num)) return null;
      return num <= 1 ? num * 100 : num;
    };
    if (candidate && typeof candidate === 'object') {
      const a = candidate?.A ?? candidate?.a ?? candidate?.p1 ?? candidate?.player_1 ?? candidate?.pctA ?? candidate?.playerA;
      const b = candidate?.B ?? candidate?.b ?? candidate?.p2 ?? candidate?.player_2 ?? candidate?.pctB ?? candidate?.playerB;
      const pctA = Math.max(0, Math.min(100, scalePct(a) ?? 0));
      const pctB = Math.max(0, Math.min(100, scalePct(b) ?? 0));
      if ((pctA || pctB) && Number.isFinite(pctA) && Number.isFinite(pctB)) return { pctA, pctB };
    }

    // 2) Parse from text heuristically
    const text = String(responseText || '')
      .replace(/[\u2012\u2013\u2014\u2015\u2212]/g, '-')
      .replace(/\r\n/g, '\n');

    // Strategy A: Look for explicit A: xx% and B: yy% in any order
    const reAB = /A\s*[:\-]?\s*(\d{1,3}(?:\.\d+)?)%[\s\S]{0,160}?B\s*[:\-]?\s*(\d{1,3}(?:\.\d+)?)%/i;
    const reBA = /B\s*[:\-]?\s*(\d{1,3}(?:\.\d+)?)%[\s\S]{0,160}?A\s*[:\-]?\s*(\d{1,3}(?:\.\d+)?)%/i;
    let m = text.match(reAB);
    if (m) {
      const pctA = Math.max(0, Math.min(100, scalePct(m[1]) ?? 0));
      const pctB = Math.max(0, Math.min(100, scalePct(m[2]) ?? 0));
      return { pctA, pctB };
    }
    m = text.match(reBA);
    if (m) {
      const pctB = Math.max(0, Math.min(100, scalePct(m[1]) ?? 0));
      const pctA = Math.max(0, Math.min(100, scalePct(m[2]) ?? 0));
      return { pctA, pctB };
    }

    // Strategy B: Two percentages mentioned near keywords like "overall win", "win rate", etc.
    const hintIdx = text.search(/overall\s*(?:win|winning|victory)\s*(?:rate|percentage|%)/i);
    if (hintIdx >= 0) {
      const windowText = text.slice(Math.max(0, hintIdx - 120), hintIdx + 240);
      const percents = [...windowText.matchAll(/(\d{1,3}(?:\.\d+)?)%/g)].map(m => Number(m[1])).filter(Number.isFinite);
      if (percents.length >= 2) {
        const [a, b] = percents;
        const pctA = Math.max(0, Math.min(100, scalePct(a) ?? 0));
        const pctB = Math.max(0, Math.min(100, scalePct(b) ?? 0));
        return { pctA, pctB };
      }
    }
  } catch {}
  return null;
};

/**
 * Format number with unit if present
 */
export const fmt = (val, unit) => {
  if (val === null || val === undefined || Number.isNaN(val)) return '-';
  const n = Number(val);
  if (!Number.isFinite(n)) return String(val);
  return unit ? `${n.toFixed(1)}${unit}` : n.toFixed(1);
};

/**
 * Shared percentage formatter: 1 decimal with % sign
 */
export const formatPct1 = (v) => `${Number(v ?? 0).toFixed(1)}%`;

/**
 * Normalize all percentage substrings in a text to 1 decimal place (e.g., 57.142857% -> 57.1%)
 */
export const normalizePercentsInText = (text) => {
  try {
    if (!text) return text;
    // Match numbers with optional decimals followed by optional spaces and %
    return String(text).replace(/(\d{1,3})(?:\.(\d+))?\s*%/g, (_m, intPart, fracPart) => {
      const num = Number(`${intPart}${fracPart ? '.' + fracPart : ''}`);
      if (!Number.isFinite(num)) return `${intPart}${fracPart ? '.' + fracPart : ''}%`;
      return `${num.toFixed(1)}%`;
    });
  } catch { return text; }
};

/**
 * Compute overall win percentage from rallies array
 */
export const computeWinPercentages = (rallies, matchStats) => {
  const arr = Array.isArray(matchStats?.rallies) ? matchStats.rallies : rallies;
  if (!Array.isArray(arr) || arr.length === 0) return null;
  let winsA = 0, winsB = 0;
  arr.forEach(r => {
    const w = r?.winner ?? r?.winner_player ?? r?.rally_winner;
    if (String(w).toUpperCase() === 'A') winsA += 1;
    else if (String(w).toUpperCase() === 'B') winsB += 1;
  });
  const total = winsA + winsB;
  if (total === 0) return { winsA, winsB, pctA: 0, pctB: 0, total };
  return {
    winsA,
    winsB,
    pctA: (winsA / total) * 100,
    pctB: (winsB / total) * 100,
    total,
  };
};

/**
 * Compute win percentage across phases using 1–25% (Initial), 26–75% (Mid), 76–100% (Final)
 */
export const computeWinPercentagePhases = (rallies, matchStats) => {
  const arr = Array.isArray(matchStats?.rallies) ? matchStats.rallies : rallies;
  if (!Array.isArray(arr) || arr.length === 0) return [
    { label: 'Initial', pctA: 0, pctB: 0, total: 0 },
    { label: 'Mid', pctA: 0, pctB: 0, total: 0 },
    { label: 'Final', pctA: 0, pctB: 0, total: 0 },
  ];
  const n = arr.length;
  const firstCut = Math.floor(n * 0.25); // 1–25%
  const secondCut = Math.floor(n * 0.75); // 26–75%
  const slices = [
    { label: 'Initial', data: arr.slice(0, firstCut) },
    { label: 'Mid', data: arr.slice(firstCut, secondCut) },
    { label: 'Final', data: arr.slice(secondCut) },
  ];
  return slices.map(s => {
    let winsA = 0, winsB = 0;
    s.data.forEach(r => {
      const w = r?.winner ?? r?.winner_player ?? r?.rally_winner;
      if (String(w).toUpperCase() === 'A') winsA += 1;
      else if (String(w).toUpperCase() === 'B') winsB += 1;
    });
    const total = winsA + winsB;
    const pctA = total ? (winsA / total) * 100 : 0;
    const pctB = total ? (winsB / total) * 100 : 0;
    return { label: s.label, pctA, pctB, total, winsA, winsB };
  });
};
